import type { ReactNode } from 'react';
import { View, Text, Pressable, Image } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { SvgXml } from 'react-native-svg';
import { Clock } from 'lucide-react-native';
import { gameCardStyles as styles } from './GameCard.styles';
import { CoinDot } from '../CoinDot/CoinDot';
import { cardAccentStyle } from '../../../src/quiz/helpers/gameDynamicStyles.helper';
import { WARMUP_COLORS, WARMUP_HUB } from '../../../src/quiz/constants/warmup.constants';

const SVG_DATA_URI_PREFIX = 'data:image/svg+xml,';

/**
 * RN core Image cannot rasterize SVG on Android/iOS (only web), so inline SVG card art
 * (e.g. Wall Breaker's SLINGSHOT_CARD_ART_URI) must render through react-native-svg.
 */
function svgXmlFromDataUri(uri: string): string | null {
  return uri.startsWith(SVG_DATA_URI_PREFIX)
    ? decodeURIComponent(uri.slice(SVG_DATA_URI_PREFIX.length))
    : null;
}

interface GameCardProps {
  live: boolean;
  accent: string;
  /** Real R2 artwork for the image banner; omit to render the gradient + icon placeholder. */
  imageUri?: string;
  /** Image-area gradient + icon for artless (coming-soon) games. */
  artGradient?: readonly [string, string];
  icon?: ReactNode;
  title: string;
  subtitle: string;
  subtitleDone?: boolean;
  soon?: boolean;
  done?: boolean;
  streak?: number;
  reward?: number;
  countdown?: string;
  onPress?: () => void;
}

/** A single Warmup hub tile: artwork banner on top, centered title/subtitle/meta below. */
export function GameCard(props: GameCardProps) {
  const svgArtXml = props.imageUri ? svgXmlFromDataUri(props.imageUri) : null;
  return (
    <Pressable
      style={({ pressed }) => [styles.cardWrap, pressed && props.live && styles.pressed]}
      onPress={props.onPress}
      disabled={!props.live}
    >
      <View style={[styles.card, !props.live && styles.cardSoon, cardAccentStyle(props.accent)]}>
        <View style={styles.imageArea}>
          {svgArtXml ? (
            <View style={styles.image} accessibilityLabel={props.title}>
              {/* "slice" = the SVG equivalent of resizeMode="cover". */}
              <SvgXml xml={svgArtXml} width="100%" height="100%" preserveAspectRatio="xMidYMid slice" />
            </View>
          ) : props.imageUri ? (
            <Image source={{ uri: props.imageUri }} style={styles.image} resizeMode="cover" accessibilityLabel={props.title} />
          ) : (
            <LinearGradient
              colors={props.artGradient ?? WARMUP_HUB.wordleCardGradient}
              start={{ x: 0.5, y: 0 }}
              end={{ x: 0.5, y: 1 }}
              style={styles.imagePlaceholder}
            >
              {props.icon}
            </LinearGradient>
          )}

          {props.soon ? (
            <View style={styles.soonBadge}><Text style={styles.soonBadgeText}>SOON</Text></View>
          ) : null}

          {props.done ? (
            <View style={styles.doneOverlay}>
              <View style={styles.donePill}><Text style={styles.donePillText}>DONE ✓</Text></View>
            </View>
          ) : null}
        </View>

        <View style={styles.textArea}>
          <Text style={styles.title} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.8}>{props.title}</Text>
          <Text style={[styles.subtitle, props.subtitleDone && styles.subtitleDone]} numberOfLines={2}>
            {props.subtitle}
          </Text>

          {props.live ? (
            <View style={styles.meta}>
              {typeof props.streak === 'number' ? (
                <>
                  <View style={styles.metaItem}>
                    <Text style={styles.fire}>🔥</Text>
                    <Text style={styles.metaStreak}>{props.streak}</Text>
                  </View>
                  <View style={styles.metaDivider} />
                </>
              ) : null}
              {props.done ? (
                <View style={styles.metaItem}>
                  <Clock size={12} color={WARMUP_COLORS.textDim} />
                  <Text style={styles.metaDone}>{`New in ${props.countdown}`}</Text>
                </View>
              ) : typeof props.reward === 'number' ? (
                <View style={styles.metaItem}>
                  <CoinDot />
                  <Text style={styles.metaReward}>{props.reward}</Text>
                </View>
              ) : null}
            </View>
          ) : null}
        </View>
      </View>
    </Pressable>
  );
}
